import {
  LoadingIndicator,
  rem,
  SuggestionItem,
  SuggestionsVariants,
} from '@triumph/design-system';
import { Suggestion } from '@triumph/design-system/components/TypeAhead/TypeAhead.types';
import { map } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  HelpText,
  LoadingContainer,
  NoResultsContainer,
  SuggestionList,
  SuggestionsContainer,
  SwitcherInput,
} from './DebtorSwitcher.StyledComponents';

interface DebtorSwitcherProps {
  id: string;
  suggestions: Suggestion[];
  onChange: (value: string) => void;
  initialValue?: string;
  onSelected?: (result: Suggestion) => void;
  isLoading?: boolean;
}

export const DebtorSwitcher: React.FC<DebtorSwitcherProps> = ({
  id,
  suggestions,
  onChange,
  initialValue = '',
  onSelected,
  isLoading = false,
}) => {
  const { t } = useTranslation('invoicesDashboard');

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionListRef = useRef<HTMLUListElement>(null);
  const activeResultRef = useRef<number | null>(null);

  const [inputValue, setInputValue] = useState<string>(initialValue);
  const [sortedResults, setSortedResults] = useState<Suggestion[]>([]);
  const [activeResult, setActiveResult] = useState<number | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focused, setFocused] = useState(false);

  // Close suggestions when clicking outside the container
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  // Sync initial value
  useEffect(() => {
    if (initialValue) {
      setInputValue(initialValue);
    }
  }, [initialValue]);

  // Sort and store suggestions
  useEffect(() => {
    setSortedResults(
      suggestions.slice()
    );
  }, [suggestions]);

  // Reset active result when input clears
  useEffect(() => {
    if (inputValue === '') {
      activeResultRef.current = null;
      setActiveResult(null);
      setSortedResults([]);
    }
  }, [inputValue]);

  // Scroll active result into view
  useEffect(() => {
    if (
      showSuggestions &&
      suggestionListRef.current &&
      activeResult !== null
    ) {
      const element = suggestionListRef.current.children[
        activeResult
      ] as HTMLElement;
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [activeResult, showSuggestions]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    onChange(value);
    setShowSuggestions(true);
  };

  const handleSelection = (result: Suggestion) => {
    setInputValue(result.value);
    setShowSuggestions(false);
    if (onSelected) {
      onSelected(result);
    }  
  };

  const handleFocus = () => {
    inputRef.current?.select();
    setFocused(true);
    setShowSuggestions(true);
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (
      e.relatedTarget == null ||
      !containerRef.current?.contains(e.relatedTarget as Node)
    ) {
      if (!inputValue && initialValue) {
        setInputValue(initialValue);
      }
      setShowSuggestions(false);
      setFocused(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (inputValue === '') return;

    const key = e.key;
    const listLength = sortedResults.length;

    if (key === 'ArrowDown') {
      e.preventDefault();
      if (!showSuggestions) {
        setShowSuggestions(true);
        activeResultRef.current = 0;
        setActiveResult(0);
      } else {
        const newIndex =
          activeResultRef.current == null
            ? 0
            : (activeResultRef.current + 1) % listLength;
        activeResultRef.current = newIndex;
        setActiveResult(newIndex);
      }
    }

    if (key === 'ArrowUp') {
      e.preventDefault();
      const lastItem = listLength - 1;
      if (!showSuggestions) {
        setShowSuggestions(true);
        activeResultRef.current = lastItem;
        setActiveResult(lastItem);
      } else {
        const newIndex =
          activeResultRef.current == null
            ? lastItem
            : (activeResultRef.current + listLength - 1) % listLength;
        activeResultRef.current = newIndex;
        setActiveResult(newIndex);
      }
    }

    if (key === 'Escape') {
      setShowSuggestions(false);
      inputRef.current?.blur();
    }

    if (key === 'Enter') {
      e.preventDefault();
      if (sortedResults.length > 0) {
        const selectedIndex =
          activeResultRef.current !== null ? activeResultRef.current : 0;
        const selectedSuggestion = sortedResults[selectedIndex];
        if (selectedSuggestion) {
          handleSelection(selectedSuggestion);
        }
      }
    }
  };

  const renderSuggestions = () => {
    if (!showSuggestions) return null;

    if (inputValue.length < 3) {
      return (
        <SuggestionsContainer>
          <HelpText>{t('debtorSwitcher.helpText')}</HelpText>
        </SuggestionsContainer>
      );
    }

    if (isLoading) {
      return (
        <SuggestionsContainer>
          <LoadingContainer>
            <LoadingIndicator show={true} />
          </LoadingContainer>
        </SuggestionsContainer>
      );
    }

    if (sortedResults.length === 0) {
      return (
        <SuggestionsContainer data-testid={`${id}-no-results`}>
          <NoResultsContainer>{t('debtorSwitcher.noResults')}</NoResultsContainer>
        </SuggestionsContainer>
      );
    }

    return (
      <SuggestionsContainer data-testid={`${id}-suggestions`}>
        <SuggestionList role="listbox" ref={suggestionListRef}>
          {map(sortedResults, (result, i) => {
            const handleSelect = () => {
              handleSelection(result);
            };

            return (
              <SuggestionItem
                key={`${id}-option-${i}`}
                id={result.id}
                suggestion={result}
                variant={SuggestionsVariants.comboline}
                onSelect={handleSelect}
                highlightedCharacters={inputValue}
                suggestionTestId={`${id}-suggestion-option-${i}`}
                maxWidth={
                  containerRef.current
                    ? rem(containerRef.current.clientWidth - 32)
                    : undefined
                }
              />
            );
          })}
        </SuggestionList>
      </SuggestionsContainer>
    );
  };

  return (
    <Container
      id={`${id}-container`}
      onBlur={handleBlur}
      ref={containerRef}
      data-testid={`${id}-container`}
    >
        <SwitcherInput
          ref={inputRef}
          id={`${id}-input`}
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={focused ? handleKeyDown : undefined}
          placeholder={t('debtorSwitcher.searchPlaceholder')}
          data-testid={`${id}-input`}
        />
      {renderSuggestions()}
    </Container>
  );
};